// Specifications for Murano ceiling lamps, read from the Shopify description.
//
// Descriptions are free Danish text written per lamp, but they mention the
// same handful of facts: size, shape, pattern, colour, fitting, socket, age,
// maker and condition. We pick those out with patterns and map them to a
// fixed vocabulary so the English page gets translated values too.
//
// Only facts the description actually states are shown — nothing is
// guessed. A group with no facts is left out.

type Copy = { da: string; en: string };

export type SpecRow = { label: Copy; value: Copy };
export type SpecGroup = { id: string; title: Copy; rows: SpecRow[] };

const c = (da: string, en: string): Copy => ({ da, en });

function firstNumber(text: string, patterns: RegExp[]): number | null {
  for (const re of patterns) {
    const m = text.match(re);
    if (m) {
      const n = Number(m[1]);
      if (n >= 10 && n <= 120) return n;
    }
  }
  return null;
}

function joinCopy(parts: Copy[], and: Copy = c("og", "and")): Copy {
  const join = (xs: string[], word: string) =>
    xs.length <= 1 ? (xs[0] ?? "") : `${xs.slice(0, -1).join(", ")} ${word} ${xs[xs.length - 1]}`;
  return c(
    join(
      parts.map((p) => p.da),
      and.da,
    ),
    join(
      parts.map((p) => p.en),
      and.en,
    ),
  );
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function muranoCeilingSpecs(description: string, title: string): SpecGroup[] {
  const d = description.toLowerCase().replace(/\s+/g, " ");
  const t = title.toLowerCase();
  // Description + title, for facts the title often carries (shape, pattern, colour).
  const dt = `${d} ${t}`;
  // Drop colour words that describe the fitting or cord, so "hvidt greb"
  // isn't read as white glass.
  const g = dt.replace(
    /(?:hvid(?:t|e)?|messingfarvet) ?(?:greb|base|ophæng|ledning|fod|afslutning)|hvidmalet[a-zæøå ]*greb|greb i hvid/g,
    "",
  );
  const groups: SpecGroup[] = [];

  // ---------- Size & shape ----------
  const size: SpecRow[] = [];
  const width =
    firstNumber(d, [
      /(\d{2,3}) ?cm i (?:diameter|diameteren|bredden)/,
      /(?:diameter|bredde) (?:på|af) (?:hele )?(\d{2,3}) ?cm/,
      /(\d{2,3}) ?cm (?:brede?|på bredeste sted)/,
      /måler (?:hele )?(\d{2,3}) ?cm(?! i højden)/,
      /(\d{2,3}) ?cm i diameter/,
      /oval,? (\d{2,3}) ?cm/,
      /(?:på|af) (?:hele )?(\d{2,3}) ?cm/,
    ]) ?? firstNumber(t, [/(\d{2,3}) ?cm/]);
  const round = /\b(?:helt )?rund\b/.test(dt);
  const oval = /\boval/.test(dt);
  if (width) {
    const isDiameter = round || /diameter/.test(d);
    size.push({
      label: isDiameter ? c("Diameter", "Diameter") : c("Bredde", "Width"),
      value: c(`${width} cm`, `${width} cm`),
    });
  }
  const height = firstNumber(d, [/(\d{2,3}) ?cm i højden/, /(\d{2,3}) ?cm høj/]);
  if (height) size.push({ label: c("Højde", "Height"), value: c(`${height} cm`, `${height} cm`) });
  if (/ufo/.test(d))
    size.push({ label: c("Form", "Shape"), value: c("Flad UFO-form", "Flat UFO shape") });
  else if (oval) size.push({ label: c("Form", "Shape"), value: c("Oval", "Oval") });
  else if (round) size.push({ label: c("Form", "Shape"), value: c("Rund", "Round") });
  if (size.length) groups.push({ id: "size", title: c("Mål & form", "Size & shape"), rows: size });

  // ---------- Glass & history ----------
  const glass: SpecRow[] = [];

  const colours: Copy[] = [];
  if (
    /regnbue|flerfarve|(?:opalhvid|lyserød|grøn|jadegrøn|blå)[a-zæøå]*,? (?:og )?(?:lyserød|grøn|jadegr[æa]n|blå|rød)/.test(
      g,
    )
  )
    colours.push(c("flerfarvet", "multicoloured"));
  else {
    if (/lyseblå|lys blå/.test(g)) colours.push(c("lyseblå", "light blue"));
    else if (/\bblå|\bblåt/.test(g)) colours.push(c("blå", "blue"));
    if (/\bgul/.test(g)) colours.push(c("gul", "yellow"));
    // "den lyserøde lampe" refers to another lamp in the photos.
    if (/lyserød(?!e lampe)|rosa|bubblegum/.test(g)) colours.push(c("lyserød", "pink"));
    if (/karamel|cremet|cremer/.test(g)) colours.push(c("creme og karamel", "cream and caramel"));
    if (!colours.length && /\bhvid/.test(g)) colours.push(c("hvid", "white"));
  }
  if (colours.length) {
    const v = joinCopy(colours);
    const clear = /gennemsigtig/.test(d);
    glass.push({
      label: c("Farve", "Colour"),
      value: c(
        cap(v.da) + (clear ? " med klart glas" : ""),
        cap(v.en) + (clear ? " with clear glass" : ""),
      ),
    });
  }

  const patterns: Copy[] = [];
  if (/peacock/.test(dt)) patterns.push(c("peacock", "peacock"));
  if (/filigran/.test(dt)) patterns.push(c("filigran", "filigree"));
  if (/swirl/.test(dt)) patterns.push(c("swirl", "swirl"));
  if (/plettet|måne/.test(dt)) patterns.push(c("plettet (måne)", "speckled (moon)"));
  if (patterns.length) {
    const v = joinCopy(patterns);
    glass.push({ label: c("Mønster", "Pattern"), value: c(cap(v.da), cap(v.en)) });
  }

  if (/de majo/.test(d))
    glass.push({
      label: c("Glaspusteri", "Glassworks"),
      value: c("De Majo, Murano", "De Majo, Murano"),
    });
  else if (/vetri/.test(d + t))
    glass.push({ label: c("Glaspusteri", "Glassworks"), value: c("Vetri Murano", "Vetri Murano") });
  else
    glass.push({ label: c("Oprindelse", "Origin"), value: c("Murano, Italien", "Murano, Italy") });

  const range = d.match(/(19[5-8]0)(?:’erne|'erne|erne)?(?: ?[/–-] ?(19[5-8]0))?/);
  if (range) {
    const [, a, b] = range;
    const decade = /(19[5-8]0)(?:’erne|'erne|erne)/.test(d);
    glass.push({
      label: c("Periode", "Period"),
      value: b
        ? c(`${a}–${b}`, `${a}–${b}`)
        : decade
          ? c(`${a}'erne`, `${a}s`)
          : c(`Ca. ${a}`, `Around ${a}`),
    });
  }

  const marks: Copy[] = [];
  if (/signer/.test(d)) marks.push(c("signeret i glasset", "signed in the glass"));
  if (/stempel/.test(d)) marks.push(c("stempel i grebet", "stamp on the fitting"));
  if (/klistermærke/.test(d))
    marks.push(
      /nogle kommer/.test(d)
        ? c("nogle har originalt klistermærke", "some have the original sticker")
        : c("originalt klistermærke", "original sticker"),
    );
  if (marks.length) {
    const v = joinCopy(marks);
    glass.push({ label: c("Mærkning", "Markings"), value: c(cap(v.da), cap(v.en)) });
  }

  if (glass.length)
    groups.push({ id: "glass", title: c("Glas & historie", "Glass & history"), rows: glass });

  // ---------- Fitting & electrics ----------
  const fit: SpecRow[] = [];
  const brass = /messing ?(?:greb|ophæng|farvet)|messinggreb|messing ?fod/.test(d);
  const white = /hvid(?:t|e)? ?(?:greb|base|ophæng)|hvidmalet|greb i hvid/.test(d);
  if (brass || white) {
    const isNew = /\bny(?:t|e)? (?:fint )?messing/.test(d);
    const original = /original(?:t|e)? (?:hvid(?:t)? |messing ?)?greb|gammelt messinggreb/.test(d);
    const patina = /patina|patineret/.test(d);
    const base = brass ? c("messinggreb", "brass fitting") : c("hvidt greb", "white fitting");
    const prefix = isNew ? c("nyt ", "new ") : original ? c("originalt ", "original ") : c("", "");
    fit.push({
      label: c("Greb", "Fitting"),
      value: c(
        cap(prefix.da + base.da) + (patina ? " med patina" : ""),
        cap(prefix.en + base.en) + (patina ? " with patina" : ""),
      ),
    });
  }
  if (/kop og wire|wire og kop/.test(d))
    fit.push({
      label: c("Ophæng", "Hanging"),
      value: /du (?:vælger|bestemmer)|kan leveres|ønskes/.test(d)
        ? c(
            "Kop og wire, eller direkte i lampeudtag — efter ønske",
            "Cup and wire, or straight from the ceiling outlet — your choice",
          )
        : c("Kop og wire", "Cup and wire"),
    });
  if (/e27/.test(d))
    fit.push({ label: c("Fatning", "Socket"), value: c("E27, 1 pære", "E27, 1 bulb") });
  if (/hvid ledning/.test(d))
    fit.push({ label: c("Ledning", "Cord"), value: c("Hvid ledning", "White cord") });
  if (fit.length)
    groups.push({ id: "fitting", title: c("Ophæng & el", "Fitting & electrics"), rows: fit });

  // ---------- Condition ----------
  const cond: SpecRow[] = [];
  if (/\bstand(?:en)?\b/.test(d)) {
    const top =
      /fornem|fortrinlig|yderst flot|sublim|fantastisk smuk i standen|rigtig flot|flot vintage|smuk vintage/.test(
        d,
      );
    const signs = /aldersspor|brugsspor/.test(d);
    cond.push({
      label: c("Stand", "Condition"),
      value: c(
        (top ? "Flot vintage-stand" : "Fin vintage-stand") +
          (signs ? " med almindelige aldersspor" : ""),
        (top ? "Excellent vintage condition" : "Good vintage condition") +
          (signs ? " with normal signs of age" : ""),
      ),
    });
  }
  if (/bobbel|prikker|tykkere|tungere|forskel på swirl/.test(d))
    cond.push({
      label: c("Håndlavet", "Handmade"),
      value: c(
        "Små variationer i glasset, som er en del af charmen",
        "Small variations in the glass that are part of the charm",
      ),
    });
  if (/stykvis|pr\.? ?styk/.test(d))
    cond.push({
      label: c("Antal", "Quantity"),
      value: /flere|sæt/.test(d)
        ? c(
            "Sælges pr. stk. — flere matchende haves",
            "Sold individually — matching ones available",
          )
        : c("Sælges pr. stk.", "Sold individually"),
    });
  if (cond.length) groups.push({ id: "condition", title: c("Stand", "Condition"), rows: cond });

  return groups;
}
